/**
 * หน้าจอ "แผนการผลิต" (`/planning/production-plan`, `WO-P2-M5-006`, D-127/D-110
 * ข้อ 8) — รายการที่ต้องผลิตจากแผน MPS รายวันเทียบยอดที่ผลิตได้จริง
 *
 * สองเส้นที่มีอยู่แล้วทั้งคู่: `GET /v1/mps-versions` (เลือกแผน) และ
 * `GET /v1/mps-attainment` (แผนเทียบของจริง, cursor 20 แถว/หน้า) — **ไม่มีปุ่มเขียน
 * ใด ๆ บนหน้านี้เลย** ทั้ง "สร้างใบสั่งผลิต" และ "ดูสถานะ" เป็นลิงก์ล้วนไป
 * `/production/orders` ไม่ยิง API ของใบสั่งผลิตจากที่นี่ (D-127 ข้อ 4)
 *
 * dropdown เลือกแผนโหลด **ทุกแผนที่ผู้ใช้เห็น** จนครบทุกหน้า (ทะเบียนเล็กแบบเดียวกับ
 * `CustomerScreen`, D-123 ข้อ 1) ส่วนตารางเทียบผลใช้ cursor ของเซิร์ฟเวอร์ตรง ๆ
 * ไม่โหลดล่วงหน้า — ข้อมูลนี้ไม่มีกรอบบนตายตัวแบบทะเบียนลูกค้า
 *
 * **D-131 (WO-P2-M5-013)** — จุดเข้าของการวางแผนแบบ Manual: ปุ่ม "สร้างแผนแบบ Manual"
 * ข้างตัวเลือกแผน (เฉพาะ `can_create`) ไปหน้าสร้างที่ `/planning/manual-plan?view=create`;
 * สร้างสำเร็จกลับมาที่นี่พร้อม router state `{ manualPlanCreated }` ที่อ่านครั้งเดียว
 * (แนวเดียวกับ `CustomerDetailView`) และลิงก์ "จัดการแผนนี้"/"รายการแผนทั้งหมด" ไปหน้า
 * `/planning/manual-plan` เดิม เพราะ Sidebar ไม่มีเมนูไปหน้านั้นแล้ว (`WO-P2-M5-014`)
 *
 * **D-145 (WO-P2-M5-022)** — ไม่มี `?mps=` แล้ว = มุมมองรายการ "ตารางแผนทั้งหมด"
 * (ทุกแผนที่โหลดไว้ครบจาก `versions` อยู่แล้ว ใหม่สุดก่อน) แทนการ auto-select แผน
 * เริ่มต้นที่เคยมี (effect นั้นถูกลบไปแล้ว — `pickDefaultMpsVersion` ยังอยู่ใน
 * `mpsView.ts` เพื่อ unit test ของมันเอง แต่หน้าจอนี้ไม่เรียกใช้แล้ว) คลิกชื่อแผน/
 * "ดูรายละเอียด" → `?mps=<id>` เข้ามุมมองรายละเอียดเดิมทุกอย่าง (dropdown, ตัวกรอง,
 * ตารางเทียบผล) ที่เพิ่มมาคือลิงก์ "← รายการแผนทั้งหมด" บนหัวหน้า
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { StatusBadge } from '../StatusBadge';
import { DateRangePicker } from '../components/DateRangePicker';
import { InfoHint } from '../components/InfoHint';
import { SortableHeader, type SortDirection } from '../components/SortableHeader';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { PRODUCTION_ORDER_MODULE } from '../../lib/productionOrders/types';
import {
  fetchMpsAttainment,
  fetchMpsVersions,
  ruleFromMpsFailure,
  type MpsFailure,
} from '../../lib/mps/client';
import {
  DRAFT_MPS_STATUS,
  MPS_VERSION_STATUSES,
  PLANNING_MODULE,
  type MpsVersionSummary,
  type MpsWorkCenterOption,
} from '../../lib/mps/types';
import {
  DEFAULT_SORT_COLUMN,
  attainmentPctLabel,
  distinctPlantCodes,
  filterMpsVersions,
  hasRemainingToProduce,
  mpsVersionOptionLabel,
  mpsVersionStatusLabel,
  mpsVersionStatusTone,
  planningDateTimeOf,
  productionOrderCreateSearch,
  productionOrderStatusSearch,
  productionPlanListPageCount,
  productionPlanListPageSlice,
  productionPlanListRowRangeLabel,
  sortMpsVersionsByCreatedAtDesc,
  sortProductionPlanRows,
  toProductionPlanRow,
  workCenterOptionLabel,
  type ProductionPlanRow,
  type ProductionPlanSortColumn,
} from '../../lib/mps/mpsView';

const ATTAINMENT_PAGE_SIZE = 20;

export interface ProductionPlanScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type VersionsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly MpsVersionSummary[] }
  | MpsFailure;

type RowsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly rows: readonly ProductionPlanRow[];
      readonly nextCursor: string | null;
      readonly workCenters: readonly MpsWorkCenterOption[];
    }
  | MpsFailure;

/** กองของ cursor ที่ผูกกับตัวกรองชุดที่มันเกิดมา — แนวเดียวกับ `SalesOrderScreen` (D-118) */
interface ScopedPaging {
  readonly key: string;
  readonly cursors: readonly string[];
}

const NO_CURSORS: readonly string[] = [];

/** router state ที่หน้าสร้างแผน Manual ส่งมาหลังบันทึกสำเร็จ (D-131 ข้อ 2) */
interface ProductionPlanLocationState {
  readonly manualPlanCreated?: { readonly versionLabel: string };
}

function failureHeading(failure: MpsFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้';
    case 'forbidden':
      return 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้';
    case 'not-found':
      return 'ไม่พบแผนนี้';
    case 'conflict':
      return 'คำขอนี้ชนกับสถานะปัจจุบัน';
    case 'rule-violation':
      return 'คำขอนี้ติดกฎทางธุรกิจ';
    case 'bad-request':
      return 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`;
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้';
    case 'invalid-response':
      return 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ';
    case 'cancelled':
      return 'ยกเลิกการเรียกข้อมูลแล้ว';
  }
}

function failureText(failure: MpsFailure): string {
  const rule = ruleFromMpsFailure(failure);
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)';
    case 'forbidden':
      return `คำขอนี้ต้องการโมดูล ${PLANNING_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่`;
    case 'not-found':
      return 'แผนนี้ไม่มีอยู่ หรืออยู่นอกขอบเขตของคุณ — สองกรณีนี้ตอบเหมือนกันโดยตั้งใจ (D-110 ข้อ 4ก)';
    case 'conflict':
      return 'เซิร์ฟเวอร์ตอบ 409';
    case 'rule-violation':
      return rule === null
        ? 'เซิร์ฟเวอร์ตอบ 422 พร้อมกฎทางธุรกิจ'
        : `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${rule} — ช่วงวันที่ที่ขอกว้างหรือกลับทางเกินกว่าที่เส้นนี้ตอบได้`;
    case 'bad-request':
      return 'ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ';
    case 'server-error':
      return 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ';
    case 'network-error':
      return failure.detail;
    case 'invalid-response':
      return 'หน้าจอปฏิเสธที่จะแสดงแถวที่อ่านได้ไม่ครบ';
    case 'cancelled':
      return 'คำขอถูกยกเลิกก่อนได้รับคำตอบ';
  }
}

interface FailureNoticeProps {
  readonly failure: MpsFailure;
  readonly testId: string;
  readonly onRetry: () => void;
}

function FailureNotice({ failure, testId, onRetry }: FailureNoticeProps) {
  return (
    <div className="cap-notice" data-testid={testId} data-failure={failure.status} role="status">
      <StatusBadge tone="danger">ทำรายการไม่สำเร็จ</StatusBadge>
      <h3 className="cap-notice__title">{failureHeading(failure)}</h3>
      <p className="cap-notice__text">{failureText(failure)}</p>
      <div className="cap-notice__actions">
        <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
          ลองอีกครั้ง
        </button>
      </div>
    </div>
  );
}

export function ProductionPlanScreen({ permissions, apiBaseUrl, fetchImpl }: ProductionPlanScreenProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const canCreateProductionOrder = can(permissions, PRODUCTION_ORDER_MODULE, 'CREATE');
  const canCreateManualPlan = can(permissions, PLANNING_MODULE, 'CREATE');
  const manualPlanCreated =
    (location.state as ProductionPlanLocationState | null)?.manualPlanCreated ?? null;

  const mpsId = searchParams.get('mps') ?? '';
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';
  const productCode = searchParams.get('product_code') ?? '';
  const lineFilter = searchParams.get('line') ?? '';

  const [versions, setVersions] = useState<VersionsState>({ status: 'loading' });
  const [versionsReloadToken, setVersionsReloadToken] = useState(0);

  const [rows, setRows] = useState<RowsState>({ status: 'loading' });
  const [rowsReloadToken, setRowsReloadToken] = useState(0);

  // ตัวเลือก "Line ผลิต" คงอยู่ข้ามการเปลี่ยนตัวกรอง/หน้า — อัปเดตเฉพาะตอนคำตอบใหม่
  // พร้อม (ด้านล่าง) ไม่ผูกกับ `rows` ตรง ๆ เพื่อไม่ให้กระพริบหายระหว่างโหลดหน้าถัดไป
  const [workCenterOptions, setWorkCenterOptions] = useState<readonly MpsWorkCenterOption[]>([]);

  const [sortColumn, setSortColumn] = useState<ProductionPlanSortColumn>(DEFAULT_SORT_COLUMN);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const filterKey = `${mpsId}|${dateFrom}|${dateTo}|${productCode}|${lineFilter}`;
  const [paging, setPaging] = useState<ScopedPaging>({ key: filterKey, cursors: NO_CURSORS });
  const cursors = paging.key === filterKey ? paging.cursors : NO_CURSORS;
  const cursor = cursors.length === 0 ? undefined : cursors[cursors.length - 1];

  // โหลด**ทุก**แผนที่ผู้ใช้เห็นจนครบทุกหน้า (D-123 ข้อ 1 — ทะเบียนเล็ก) ใช้ทั้งเป็น
  // ตัวเลือกของ dropdown (มุมมองรายละเอียด) และแถวของตารางรายการทั้งหมด (D-145)
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setVersions({ status: 'loading' });

    const loadAll = async () => {
      const items: MpsVersionSummary[] = [];
      let nextCursor: string | undefined;
      for (;;) {
        const result = await fetchMpsVersions(
          { limit: 200, ...(nextCursor === undefined ? {} : { cursor: nextCursor }) },
          { apiBaseUrl, fetchImpl, signal: controller.signal },
        );
        if (!active || result.status === 'cancelled') {
          return;
        }
        if (result.status !== 'ready') {
          setVersions(result);
          return;
        }
        items.push(...result.page.items);
        if (result.page.next_cursor === null) {
          break;
        }
        nextCursor = result.page.next_cursor;
      }
      if (active) {
        setVersions({ status: 'ready', items });
      }
    };

    void loadAll();
    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, fetchImpl, versionsReloadToken]);

  // ตัวกรองของมุมมองรายการ "ตารางแผนทั้งหมด" (D-146) — เฉพาะเมื่อ `?mps=` ว่าง ทำฝั่งหน้าจอ
  // ช่วงวันที่เทียบ released_at ไม่ใช่ created_at อีกแล้ว (D-161) — param เปลี่ยนเป็น released_from/released_to
  const listFilterQ = searchParams.get('q') ?? '';
  const listFilterStatus = searchParams.get('status') ?? '';
  const listFilterPlant = searchParams.get('plant') ?? '';
  const listFilterReleasedFrom = searchParams.get('released_from') ?? '';
  const listFilterReleasedTo = searchParams.get('released_to') ?? '';
  const listFilterKey = `${listFilterQ}|${listFilterStatus}|${listFilterPlant}|${listFilterReleasedFrom}|${listFilterReleasedTo}`;
  const hasActiveListFilters =
    listFilterQ !== '' ||
    listFilterStatus !== '' ||
    listFilterPlant !== '' ||
    listFilterReleasedFrom !== '' ||
    listFilterReleasedTo !== '';

  // มุมมองรายการ "ตารางแผนทั้งหมด" (D-145) — ไม่มี `?mps=` ไม่เลือกแผนเริ่มต้นให้เองอีก
  const [listPageIndex, setListPageIndex] = useState(0);
  useEffect(() => {
    setListPageIndex(0);
  }, [versionsReloadToken, listFilterKey]);

  useEffect(() => {
    if (mpsId === '') {
      setRows({ status: 'ready', rows: [], nextCursor: null, workCenters: [] });
      return;
    }

    let active = true;
    const controller = new AbortController();
    setRows((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchMpsAttainment(
      {
        mps_id: mpsId,
        ...(dateFrom === '' ? {} : { date_from: dateFrom }),
        ...(dateTo === '' ? {} : { date_to: dateTo }),
        ...(productCode === '' ? {} : { product_code: productCode }),
        ...(lineFilter === '' ? {} : { work_center_code: lineFilter }),
        limit: ATTAINMENT_PAGE_SIZE,
        ...(cursor === undefined ? {} : { cursor }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setRows(result);
        return;
      }
      setRows({
        status: 'ready',
        rows: result.page.items.map(toProductionPlanRow),
        nextCursor: result.page.next_cursor,
        workCenters: result.page.work_centers,
      });
    });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, fetchImpl, mpsId, dateFrom, dateTo, productCode, lineFilter, cursor, rowsReloadToken]);

  // เปลี่ยนแผน → ล้างตัวเลือก Line (D-128 ข้อ 4) — คำตอบของแผนใหม่จะเติมตัวเลือกกลับมา
  useEffect(() => {
    setWorkCenterOptions([]);
  }, [mpsId]);

  // อัปเดตตัวเลือก Line เฉพาะตอนคำตอบพร้อมจริง — ไม่ผูกกับ `rows` ระหว่างโหลดหน้าถัดไป
  // จึงไม่กระพริบหาย (D-128 ข้อ 4)
  useEffect(() => {
    if (rows.status === 'ready') {
      setWorkCenterOptions(rows.workCenters);
    }
  }, [rows]);

  // เปลี่ยนตัวกรอง → `filterKey` เปลี่ยนตาม → `cursors` ตกกลับไป `NO_CURSORS` เอง
  // (ดูนิยามด้านบน) จึงไม่ต้อง reset `paging` ตรงนี้ซ้ำ
  const applyFilter = (change: (params: URLSearchParams) => void) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        change(next);
        return next;
      },
      { replace: true },
    );
  };

  const resetListFilters = () => {
    applyFilter((params) => {
      params.delete('q');
      params.delete('status');
      params.delete('plant');
      params.delete('released_from');
      params.delete('released_to');
    });
  };

  const selectedVersion =
    versions.status === 'ready' ? versions.items.find((item) => item.mps_id === mpsId) ?? null : null;

  // ซ่อน select เมื่อไม่มีตัวเลือกและไม่มี `line` ใน URL (D-128 ข้อ 3) — รหัสที่ไม่รู้จัก
  // ใน URL ยังต้องแสดงด้วยป้ายรหัสเฉย ๆ ไม่หายเงียบ (D-128 ข้อ 4)
  const knownLineOption = workCenterOptions.find((option) => option.work_center_code === lineFilter);
  const showLineFilter = workCenterOptions.length > 0 || lineFilter !== '';

  const sortedRows =
    rows.status === 'ready'
      ? sortProductionPlanRows(rows.rows, sortColumn, sortDirection, selectedVersion?.released_at ?? null)
      : [];

  const handleSort = (columnKey: string, direction: SortDirection) => {
    setSortColumn(columnKey as ProductionPlanSortColumn);
    setSortDirection(direction);
  };

  const firstRowNumber = cursors.length * ATTAINMENT_PAGE_SIZE + 1;
  const lastRowNumber = firstRowNumber + sortedRows.length - 1;
  const rowRangeLabel = sortedRows.length === 0 ? '' : `แถว ${firstRowNumber}–${lastRowNumber}`;

  // มุมมองรายการ "ตารางแผนทั้งหมด" (D-145) — แบ่งหน้าฝั่งจอเพราะ `versions` โหลดครบแล้ว
  const sortedVersions = versions.status === 'ready' ? sortMpsVersionsByCreatedAtDesc(versions.items) : [];
  // ตัวกรองของมุมมองรายการ (D-146) — บน sortedVersions ก่อนแบ่งหน้า
  const filteredVersions = filterMpsVersions(sortedVersions, {
    q: listFilterQ,
    status: listFilterStatus,
    plant: listFilterPlant,
    releasedFrom: listFilterReleasedFrom,
    releasedTo: listFilterReleasedTo,
  });
  const listPlantOptions = versions.status === 'ready' ? distinctPlantCodes(versions.items) : [];
  const listPageCount = productionPlanListPageCount(filteredVersions.length);
  const listPageItems = productionPlanListPageSlice(filteredVersions, listPageIndex);
  const listRowRangeLabel = productionPlanListRowRangeLabel(filteredVersions.length, listPageIndex);

  return (
    <div className="cap-page" data-testid="production-plan">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            แผนการผลิต (Production Plan){' '}
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  รายการที่ต้องผลิตจากแผน MPS รายวันเทียบยอดที่ผลิตได้จริง —
                  &quot;คงเหลือที่ต้องผลิต&quot; = max(0, แผน − ผลิตได้) คำนวณที่หน้าจอ
                  ไม่เก็บ (D-127 ข้อ 3) ส่วน &quot;ผลิตได้&quot; คือ qty_good ของ
                  operation สุดท้ายของใบสั่งผลิตแต่ละใบ ไม่ใช่ยอดรวมทุก operation
                  (D-110 ข้อ 2)
                </>
              }
            />
          </h1>
        </div>
        {mpsId === '' ? null : (
          <div className="cap-page__actions">
            <Link
              className="cap-button cap-button--ghost"
              data-testid="production-plan-list-back"
              to="/planning/production-plan"
            >
              ← รายการแผนทั้งหมด
            </Link>
          </div>
        )}
      </div>

      {manualPlanCreated === null ? null : (
        <div className="cap-notice" data-testid="production-plan-manual-plan-created" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <p className="cap-notice__text">
            สร้างแผน <code className="cap-table__code">{manualPlanCreated.versionLabel}</code> แล้ว —{' '}
            {mpsVersionStatusLabel(DRAFT_MPS_STATUS)}
          </p>
        </div>
      )}

      <div className="cap-filters" data-testid="production-plan-toolbar">
        {mpsId === '' ? null : (
          <label className="cap-field">
            <span className="cap-field__label">แผน (MPS)</span>
            <select
              className="cap-input"
              data-testid="production-plan-version-select"
              value={mpsId}
              onChange={(event) => {
                applyFilter((params) => {
                  params.set('mps', event.target.value);
                  params.delete('line');
                });
              }}
            >
              <option value="">— เลือกแผน —</option>
              {versions.status === 'ready'
                ? versions.items.map((item) => (
                    <option key={item.mps_id} value={item.mps_id}>
                      {mpsVersionOptionLabel(item)}
                    </option>
                  ))
                : null}
            </select>
          </label>
        )}

        {canCreateManualPlan ? (
          <Link
            className="cap-button cap-button--primary"
            data-testid="production-plan-manual-plan-create"
            to="/planning/manual-plan?view=create"
          >
            สร้างแผนแบบ Manual
          </Link>
        ) : null}

        {selectedVersion === null ? null : (
          <div className="cap-field" data-testid="production-plan-version-status">
            <span className="cap-field__label">สถานะแผน</span>
            <span className="cap-field__status-row">
              <StatusBadge tone={mpsVersionStatusTone(selectedVersion.status)}>
                {mpsVersionStatusLabel(selectedVersion.status)}
              </StatusBadge>
              {selectedVersion.status === DRAFT_MPS_STATUS ? (
                <InfoHint
                  label="เกี่ยวกับสถานะนี้"
                  text="แผนที่ยังไม่ได้อนุมัติ — ต้องให้ผู้อื่นอนุมัติก่อนจึงปล่อยใช้ได้"
                />
              ) : null}
            </span>
          </div>
        )}

        <div className="cap-field">
          <span className="cap-field__label">จัดการแผน</span>
          <span className="cap-field__status-row">
            {selectedVersion === null ? null : (
              <Link
                className="cap-table__link"
                data-testid="production-plan-manual-plan-detail"
                to={`/planning/manual-plan?view=detail&mps=${selectedVersion.mps_id}`}
              >
                จัดการแผนนี้
              </Link>
            )}
            <Link className="cap-table__link" data-testid="production-plan-manual-plan-list" to="/planning/manual-plan">
              รายการแผนทั้งหมด
            </Link>
          </span>
        </div>

        {mpsId !== '' ? null : (
          <label className="cap-field">
            <span className="cap-field__label">ชื่อแผน</span>
            <input
              className="cap-input"
              type="search"
              data-testid="production-plan-list-filter-q"
              value={listFilterQ}
              autoComplete="off"
              onChange={(event) => {
                const value = event.target.value;
                applyFilter((params) => {
                  if (value.trim() === '') {
                    params.delete('q');
                  } else {
                    params.set('q', value);
                  }
                });
              }}
            />
          </label>
        )}

        {mpsId !== '' ? null : (
          <label className="cap-field">
            <span className="cap-field__label">สถานะ</span>
            <select
              className="cap-input"
              data-testid="production-plan-list-filter-status"
              value={listFilterStatus}
              onChange={(event) => {
                const value = event.target.value;
                applyFilter((params) => {
                  if (value === '') {
                    params.delete('status');
                  } else {
                    params.set('status', value);
                  }
                });
              }}
            >
              <option value="">ทุกสถานะ</option>
              {MPS_VERSION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {mpsVersionStatusLabel(status)}
                </option>
              ))}
            </select>
          </label>
        )}

        {mpsId !== '' || listPlantOptions.length <= 1 ? null : (
          <label className="cap-field">
            <span className="cap-field__label">โรงงาน</span>
            <select
              className="cap-input"
              data-testid="production-plan-list-filter-plant"
              value={listFilterPlant}
              onChange={(event) => {
                const value = event.target.value;
                applyFilter((params) => {
                  if (value === '') {
                    params.delete('plant');
                  } else {
                    params.set('plant', value);
                  }
                });
              }}
            >
              <option value="">ทุกโรงงาน</option>
              {listPlantOptions.map((plantCode) => (
                <option key={plantCode} value={plantCode}>
                  {plantCode}
                </option>
              ))}
            </select>
          </label>
        )}

        {mpsId !== '' ? null : (
          <DateRangePicker
            label="ช่วงวันที่ปล่อยแผน"
            from={listFilterReleasedFrom}
            to={listFilterReleasedTo}
            testId="production-plan-list-filter-released"
            onChange={(from, to) => {
              applyFilter((params) => {
                if (from === '') {
                  params.delete('released_from');
                } else {
                  params.set('released_from', from);
                }
                if (to === '') {
                  params.delete('released_to');
                } else {
                  params.set('released_to', to);
                }
              });
            }}
          />
        )}

        {mpsId !== '' || !hasActiveListFilters ? null : (
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="production-plan-list-filter-reset"
            onClick={resetListFilters}
          >
            รีเซ็ตตัวกรอง
          </button>
        )}

        {mpsId === '' ? null : (
          <DateRangePicker
            label="ช่วงวันที่"
            from={dateFrom}
            to={dateTo}
            testId="production-plan-date-range"
            onChange={(from, to) => {
              applyFilter((params) => {
                if (from === '') {
                  params.delete('date_from');
                } else {
                  params.set('date_from', from);
                }
                if (to === '') {
                  params.delete('date_to');
                } else {
                  params.set('date_to', to);
                }
              });
            }}
          />
        )}

        {mpsId === '' ? null : (
          <label className="cap-field">
            <span className="cap-field__label">รหัสสินค้า</span>
            <input
              className="cap-input"
              type="search"
              data-testid="production-plan-product-filter"
              value={productCode}
              autoComplete="off"
              onChange={(event) => {
                const value = event.target.value;
                applyFilter((params) => {
                  if (value.trim() === '') {
                    params.delete('product_code');
                  } else {
                    params.set('product_code', value);
                  }
                });
              }}
            />
          </label>
        )}

        {mpsId !== '' && showLineFilter ? (
          <label className="cap-field">
            <span className="cap-field__label">
              Line ผลิต{' '}
              <InfoHint
                label="ความหมายของ Line ผลิต"
                text="Line = ศูนย์งานที่สินค้าผ่านใน Routing (D-128 ข้อ 1)"
              />
            </span>
            <select
              className="cap-input"
              data-testid="production-plan-line-select"
              value={lineFilter}
              onChange={(event) => {
                const value = event.target.value;
                applyFilter((params) => {
                  if (value === '') {
                    params.delete('line');
                  } else {
                    params.set('line', value);
                  }
                });
              }}
            >
              <option value="">ทุก Line</option>
              {workCenterOptions.map((option) => (
                <option key={option.work_center_code} value={option.work_center_code}>
                  {workCenterOptionLabel(option)}
                </option>
              ))}
              {lineFilter !== '' && knownLineOption === undefined ? (
                <option value={lineFilter}>{lineFilter}</option>
              ) : null}
            </select>
          </label>
        ) : null}
      </div>

      {versions.status === 'loading' ? (
        <div className="cap-notice" data-testid="production-plan-versions-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการแผน…</p>
        </div>
      ) : null}

      {versions.status !== 'loading' && versions.status !== 'ready' ? (
        <FailureNotice
          failure={versions}
          testId="production-plan-versions-failure"
          onRetry={() => {
            setVersionsReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {versions.status === 'ready' && versions.items.length === 0 ? (
        <div className="cap-notice" data-testid="production-plan-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีแผนการผลิต</StatusBadge>
          <p className="cap-notice__text">เซิร์ฟเวอร์ตอบสำเร็จแต่ยังไม่มีแผน MPS ในโรงงานที่คุณเข้าถึงได้</p>
        </div>
      ) : null}

      {versions.status === 'ready' && versions.items.length > 0 && mpsId !== '' && selectedVersion === null ? (
        <div className="cap-notice" data-testid="production-plan-version-unknown" role="status">
          <StatusBadge tone="warning">ไม่พบแผนนี้</StatusBadge>
          <p className="cap-notice__text">แผนที่ระบุใน URL ไม่อยู่ในรายการที่คุณเข้าถึงได้ — เลือกแผนใหม่จาก dropdown</p>
        </div>
      ) : null}

      {versions.status === 'ready' && versions.items.length > 0 && mpsId === '' && filteredVersions.length === 0 ? (
        <div className="cap-notice" data-testid="production-plan-list-filter-empty" role="status">
          <StatusBadge tone="neutral">ไม่พบแผนที่ตรงกับตัวกรอง</StatusBadge>
          <p className="cap-notice__text">ลองปรับตัวกรอง หรือกดรีเซ็ตเพื่อดูแผนทั้งหมด</p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="production-plan-list-filter-empty-reset"
              onClick={resetListFilters}
            >
              รีเซ็ตตัวกรอง
            </button>
          </div>
        </div>
      ) : null}

      {versions.status === 'ready' && filteredVersions.length > 0 && mpsId === '' ? (
        <section className="cap-card cap-card--table" aria-label="ตารางแผนทั้งหมด">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="production-plan-list-table">
              <thead>
                <tr>
                  <th scope="col">ชื่อแผน</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">ช่วงแผน</th>
                  <th scope="col">วันที่ปล่อยแผน</th>
                  <th scope="col">การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {listPageItems.map((item) => (
                  <tr key={item.mps_id} data-testid={`production-plan-list-row-${item.mps_id}`}>
                    <th scope="row" className="cap-table__row-head">
                      <Link
                        className="cap-table__link"
                        data-testid={`production-plan-list-name-${item.mps_id}`}
                        to={`/planning/production-plan?mps=${item.mps_id}`}
                      >
                        {item.version_label}
                      </Link>
                    </th>
                    <td>
                      <code className="cap-table__code">{item.plant_code}</code>
                    </td>
                    <td>
                      <StatusBadge tone={mpsVersionStatusTone(item.status)}>
                        {mpsVersionStatusLabel(item.status)}
                      </StatusBadge>
                    </td>
                    <td>
                      {item.horizon_start}–{item.horizon_end}
                    </td>
                    <td>
                      {item.released_at === null ? (
                        <span className="cap-table__muted">—</span>
                      ) : (
                        <code className="cap-table__code">{planningDateTimeOf(item.released_at)}</code>
                      )}
                    </td>
                    <td>
                      <Link
                        className="cap-button cap-button--ghost"
                        data-testid={`production-plan-list-detail-${item.mps_id}`}
                        to={`/planning/production-plan?mps=${item.mps_id}`}
                      >
                        ดูรายละเอียด
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {listPageCount > 1 ? (
            <div className="cap-card__foot cap-pager" data-testid="production-plan-list-pager">
              <span className="cap-pager__count">{listRowRangeLabel}</span>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="production-plan-list-page-prev"
                disabled={listPageIndex === 0}
                onClick={() => {
                  setListPageIndex((index) => Math.max(0, index - 1));
                }}
              >
                ก่อนหน้า
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="production-plan-list-page-next"
                disabled={listPageIndex >= listPageCount - 1}
                onClick={() => {
                  setListPageIndex((index) => Math.min(listPageCount - 1, index + 1));
                }}
              >
                ถัดไป
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {mpsId !== '' && rows.status === 'loading' ? (
        <div className="cap-notice" data-testid="production-plan-rows-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการที่ต้องผลิต…</p>
        </div>
      ) : null}

      {mpsId !== '' && rows.status !== 'loading' && rows.status !== 'ready' ? (
        <FailureNotice
          failure={rows}
          testId="production-plan-rows-failure"
          onRetry={() => {
            setRowsReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {mpsId !== '' && rows.status === 'ready' && sortedRows.length === 0 ? (
        <div className="cap-notice" data-testid="production-plan-rows-empty" role="status">
          <p className="cap-notice__text">ไม่มีแถวที่ตรงกับตัวกรองนี้</p>
        </div>
      ) : null}

      {mpsId !== '' && rows.status === 'ready' && sortedRows.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="รายการที่ต้องผลิต">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="production-plan-table">
              <thead>
                <tr>
                  <SortableHeader
                    columnKey="product_code"
                    label="รหัสสินค้า"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-product_code"
                  />
                  <SortableHeader
                    columnKey="product_name"
                    label="Part Name"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-product_name"
                  />
                  <SortableHeader
                    columnKey="model"
                    label="Model"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-model"
                  />
                  <SortableHeader
                    columnKey="bucket_date"
                    label="วันที่"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-bucket_date"
                  />
                  <SortableHeader
                    columnKey="planned_qty"
                    label="แผน"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-planned_qty"
                  />
                  <SortableHeader
                    columnKey="actual_good_qty"
                    label="ผลิตได้"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-actual_good_qty"
                  />
                  <SortableHeader
                    columnKey="remaining_qty"
                    label="คงเหลือที่ต้องผลิต"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-remaining_qty"
                  />
                  <SortableHeader
                    columnKey="attainment_pct"
                    label="% ทำได้"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-attainment_pct"
                  />
                  <SortableHeader
                    columnKey="plant_code"
                    label="โรงงาน"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-plant_code"
                  />
                  <SortableHeader
                    columnKey="planning_date"
                    label="วันที่ปล่อยแผน"
                    activeKey={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    testId="production-plan-sort-planning_date"
                  />
                  <th scope="col">การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const showCreate = canCreateProductionOrder && hasRemainingToProduce(row.remainingQty);
                  return (
                    <tr
                      key={`${row.item.product_code}-${row.item.bucket_date}`}
                      data-testid={`production-plan-row-${row.item.product_code}-${row.item.bucket_date}`}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{row.item.product_code}</code>
                      </th>
                      <td data-testid={`production-plan-name-${row.item.product_code}-${row.item.bucket_date}`}>
                        {row.item.product_name === '' ? (
                          <span className="cap-table__muted">—</span>
                        ) : (
                          row.item.product_name
                        )}
                      </td>
                      <td data-testid={`production-plan-model-${row.item.product_code}-${row.item.bucket_date}`}>
                        {row.item.model === null ? <span className="cap-table__muted">—</span> : row.item.model}
                      </td>
                      <td>
                        <code className="cap-table__code">{row.item.bucket_date}</code>
                      </td>
                      <td>{row.item.planned_qty}</td>
                      <td>{row.item.actual_good_qty}</td>
                      <td data-testid={`production-plan-remaining-${row.item.product_code}-${row.item.bucket_date}`}>
                        {row.remainingQty}
                      </td>
                      <td>{attainmentPctLabel(row.item.attainment_pct)}</td>
                      <td>
                        <code className="cap-table__code">{row.item.plant_code}</code>
                      </td>
                      <td data-testid={`production-plan-planning-date-${row.item.product_code}-${row.item.bucket_date}`}>
                        {selectedVersion === null || selectedVersion.released_at === null ? (
                          <span className="cap-table__muted">—</span>
                        ) : (
                          <code className="cap-table__code">{planningDateTimeOf(selectedVersion.released_at)}</code>
                        )}
                      </td>
                      <td>
                        {showCreate ? (
                          <Link
                            className="cap-button cap-button--ghost"
                            data-testid={`production-plan-create-${row.item.product_code}-${row.item.bucket_date}`}
                            to={`/production/orders${productionOrderCreateSearch({
                              plantCode: row.item.plant_code,
                              productCode: row.item.product_code,
                              plannedQuantity: row.remainingQty,
                              bucketDate: row.item.bucket_date,
                            })}`}
                          >
                            สร้างใบสั่งผลิต
                          </Link>
                        ) : null}{' '}
                        <Link
                          className="cap-button cap-button--ghost"
                          data-testid={`production-plan-status-${row.item.product_code}-${row.item.bucket_date}`}
                          to={`/production/orders${productionOrderStatusSearch({
                            productCode: row.item.product_code,
                            plantCode: row.item.plant_code,
                          })}`}
                        >
                          ดูสถานะ
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="cap-card__foot cap-pager" data-testid="production-plan-pager">
            <span className="cap-pager__count">{rowRangeLabel}</span>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="production-plan-page-prev"
              disabled={cursors.length === 0}
              onClick={() => {
                setPaging({ key: filterKey, cursors: cursors.slice(0, -1) });
              }}
            >
              ก่อนหน้า
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="production-plan-page-next"
              disabled={rows.status !== 'ready' || rows.nextCursor === null}
              onClick={() => {
                if (rows.status === 'ready' && rows.nextCursor !== null) {
                  setPaging({ key: filterKey, cursors: [...cursors, rows.nextCursor] });
                }
              }}
            >
              ถัดไป
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
